/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { FC, useMemo, useState } from 'react'
import { Col, Select } from 'antd'
import { Formik, FieldProps, validateYupSchema, yupToFormErrors, FormikErrors } from 'formik'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../../context/auth-context'
import { ContractChangeWithObjectPayload, ContractChangePayload } from './types'
import { useDateTime } from '../../../shared/useDateTime'
import { FormItem, FormikSelect } from '../../../components/formik-component'
import { FormikContractChangePayload } from './formik/FormikContractChangePayload'
import { PerModal } from '../../../components/custom_components/Modal'
import useCreateOrUpdateChange from '../../../graphql/change/hooks/useCreateOrUpdateChange'
import PerFlexRow from '../../../components/custom_components/FlexRow'
import PerText from '../../../components/custom_components/Text'
import {
  ChangeInput,
  LatestContractDtoFragment,
  ContractChangeValidationListFragment,
  CareerPathEvaluation,
  FullMeetingFragment,
  GovernmentJobTitle,
  GovernmentJobTitleTinyInput,
  CountrySettingsFragment,
  Currency,
  EmployeeLevelEnum,
  MeetingType,
} from '../../../types'
import { getContractChangeFormValidationSchema } from './validations/contractChangeFormValidationScheme'
import { getItalianContractChangeFormValidationSchema } from './validations/italianContractChangeFormValidationScheme'
import { getValidationErrors } from './validations/onSubmitErrorResponseParser'
import useIsFeatureEnabled from '../../../graphql/featureToggle/useIsFeatureEnabled'
import { FeatureNameEnum } from '../../../graphql/featureToggle/FeatureNameEnum'
import { LoadingAndErrors } from '../../../components/custom_components/LoadingAndErrors'
import { getSimpleContractChangeFormValidationSchema } from './validations/simpleContractChangeFormValidationScheme'
import { ObjectSchema } from 'yup'
import { generateAvailableMonths } from './contractChangeModalHelper'
import { ConfirmTargetBonusAmountModal } from './ConfirmTargetBonusAmountModal'
import { getBrazilianContractChangeFormValidationSchema } from './validations/brazilianContractChangeFormValidationScheme'
import { getSimpleBrazilianContractChangeFormValidationSchema } from './validations/simpleBrazilianContractChangeFormValidationScheme'
import { isGovernmentJobTitleValid, MULTI_VALUE_SEPARATOR } from './helpers'
import { useCurrency } from '../../../context/currency-context'
import { retrieveCurrencyConversionRate } from '../../../shared/currencyConversionUtil'
import { showNotification } from '../../../components/custom_components/Notification/notification'
import useDeleteChange from '../../../graphql/change/hooks/useDeleteChange'
import PerSpacer from '../../../components/custom_components/Spacer'

const { Option } = Select

export interface ContractChangeModalProps {
  meeting: FullMeetingFragment
  currentContract: LatestContractDtoFragment
  change: ContractChangeWithObjectPayload
  visible: boolean
  closeModal: () => void
  datesOfCurrentChanges: Date[]
  careerPathEvaluation?: CareerPathEvaluation | null
  scheduledChanges: string
  isEdit: boolean
  isDeleting: boolean
  isPreparation: boolean
  editedDate?: string
  contractChangeValidationList: ContractChangeValidationListFragment
  hasNationalContractReference: boolean
  isEmployeeLevelValidationEnabled: boolean
  governmentJobTitlesByHiringOffice?: GovernmentJobTitle[]
  countrySettings: CountrySettingsFragment
  employeeLevels: EmployeeLevelEnum[]
}
export type InnerFieldProps = FieldProps & { values: ContractChangePayload }

export const ContractChangeModal: FC<ContractChangeModalProps> = ({
  meeting,
  currentContract,
  change,
  visible,
  closeModal,
  datesOfCurrentChanges,
  careerPathEvaluation,
  scheduledChanges,
  isEdit,
  isDeleting,
  isPreparation = false,
  editedDate,
  contractChangeValidationList,
  hasNationalContractReference,
  isEmployeeLevelValidationEnabled,
  governmentJobTitlesByHiringOffice,
  countrySettings,
  employeeLevels,
}) => {
  const employeeId: number = meeting.employee.id
  const appraisalSeasonYear = meeting.appraisalSeason.year
  const { user } = useAuth()
  const [t] = useTranslation()
  const { createOrUpdateChange } = useCreateOrUpdateChange(employeeId, currentContract.legalEntity.country)
  const { deleteChange } = useDeleteChange(employeeId, meeting.id)
  const { isFeatureEnabled, loading, error } = useIsFeatureEnabled(FeatureNameEnum.CHANGE_BE_VALIDATION)

  const [isConfirmTargetBonusModalOpen, setIsConfirmTargetBonusModalOpen] = useState<boolean>(false)
  const [invalidGovernmentJobTitle, toggleInvalidGovernmentJobTitle] = useState<boolean>(
    !!change.changes.governmentJobTitle &&
      !isGovernmentJobTitleValid(governmentJobTitlesByHiringOffice || [], change.changes.governmentJobTitle)
      ? true
      : false,
  )
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const { currencyConversionData } = useCurrency()
  const { formatDateToUtcString, utcDateStringToMoment } = useDateTime(user)

  const reset = () => {
    closeModal()
    setIsConfirmTargetBonusModalOpen(false)
  }

  const availableMonths = generateAvailableMonths(
    countrySettings.hasNationalContractReferenceLevel,
    countrySettings.hasGovernmentJobTitleReferenceLevel,
    utcDateStringToMoment,
    meeting,
    currentContract,
    scheduledChanges,
    change,
    datesOfCurrentChanges,
    editedDate,
  )

  const initialValues: ContractChangeWithObjectPayload = useMemo(() => {
    const editDate = change.validFrom ? new Date(change.validFrom) : undefined
    return {
      ...change,
      validFrom: isEdit && editDate ? `${editDate.getUTCMonth()},${editDate.getUTCFullYear()}` : undefined,
    }
  }, [change, isEdit])

  if (isFeatureEnabled === undefined || loading || error) {
    return <LoadingAndErrors hasNoData={isFeatureEnabled === undefined} isLoading={loading} error={error} />
  }

  const unknownGovernmentJobTitleCurrencyConversions: Currency[] =
    currentContract.contractGovernmentJobTitle &&
    !!governmentJobTitlesByHiringOffice &&
    !!governmentJobTitlesByHiringOffice.length &&
    currentContract.annualSalary !== undefined &&
    currentContract.annualSalary !== null
      ? [
          ...new Set(
            governmentJobTitlesByHiringOffice
              .filter(
                (gjt) =>
                  gjt.currency !== currentContract.annualSalary!.currency &&
                  retrieveCurrencyConversionRate(gjt.currency, currentContract.annualSalary!.currency, currencyConversionData) ===
                    undefined,
              )
              .map((gjt) => gjt.currency),
          ),
        ]
      : []

  return (
    <>
      {visible && (
        <Formik
          initialValues={initialValues}
          enableReinitialize
          validateOnBlur={false}
          validateOnChange={false}
          onSubmit={async (values: ContractChangeWithObjectPayload, { setSubmitting, resetForm, setErrors }) => {
            // This catches the case when the user first adds input to the filed and then
            // deletes it entirely
            if (typeof values.changes.bonus === 'string') {
              if (values.changes.bonus === '' || isNaN(values.changes.bonus)) {
                values.changes.bonus = undefined
              } else {
                values.changes.bonus = parseInt(values.changes.bonus)
              }
            } else if (typeof values.changes.bonus === 'number' && values.changes.bonus < 0) {
              values.changes.bonus = 0
            }
            if (typeof values.changes.salary === 'string') {
              if (values.changes.salary === '' || isNaN(values.changes.salary)) {
                values.changes.salary = undefined
              } else {
                values.changes.salary = parseInt(values.changes.salary)
              }
            } else if (typeof values.changes.salary === 'number' && values.changes.salary < 0) {
              values.changes.salary = 0
            }

            // set date with selected month and year, day = first of month
            const [selectedMonth, selectedYear] = values.validFrom ? values.validFrom.split(',') : [undefined, undefined]
            const selectedMonthNumber = selectedMonth ? Number(selectedMonth) : undefined
            const selectedYearNumber = selectedYear ? Number(selectedYear) : undefined
            const validFromDate: Date | undefined =
              !!selectedYearNumber && (!!selectedMonthNumber || selectedMonthNumber === 0)
                ? new Date(Date.UTC(selectedYearNumber, selectedMonthNumber, 1, 12))
                : undefined

            const validFromDateUtcString = validFromDate ? formatDateToUtcString(validFromDate) : undefined
            const updatesRestrictedTo = currentContract.updatesRestrictedTo ? new Date(currentContract.updatesRestrictedTo) : undefined

            let governmentJobTitle: GovernmentJobTitleTinyInput | undefined = undefined
            if (!!values.changes.governmentJobTitle) {
              const splitData: string[] = values.changes.governmentJobTitle.split(MULTI_VALUE_SEPARATOR)
              governmentJobTitle = {
                hiringOfficeCode: splitData[0],
                department: splitData[1],
                title: splitData[2],
              }
            }

            const input: ChangeInput = {
              id: values.id,
              type: 'ContractChange',
              changes: { ...values.changes, governmentJobTitle: governmentJobTitle },
              validFrom: validFromDateUtcString,
            }

            //clean changes if something was set which wasn't supposed to be
            if (!!updatesRestrictedTo && validFromDate && validFromDate < updatesRestrictedTo) {
              input.changes = {
                bonus: values.changes.bonus,
                employeeLevel: undefined,
                nationalReferenceLevel: undefined,
                governmentJobTitle: undefined,
                salary: undefined,
              }
            }
            const isTargetBonusChangeNotAllowed =
              selectedYearNumber && selectedYearNumber < appraisalSeasonYear && meeting.type === MeetingType.AppraisalUpdate
            if (isTargetBonusChangeNotAllowed) {
              input.changes = {
                ...input.changes,
                bonus: undefined,
              }
            }

            if (isDeleting) {
              const res = await deleteChange(change.id!)
              if (!!res.errors && !!res.errors.length) {
                setDeleteError(res.errors[0].message)
              } else {
                showNotification({
                  type: 'success',
                  message: t('appraisalMeeting.contract.changes.notifications.delete.success.title'),
                  description: t('appraisalMeeting.contract.changes.notifications.delete.success.description'),
                })
                resetForm()
                reset()
              }
            } else {
              const res = await createOrUpdateChange(input, appraisalSeasonYear, true, true, meeting.id)

              setSubmitting(false)

              if (res.data && res.data.createOrUpdateChange.success) {
                const operation = isEdit ? 'update' : 'create'
                showNotification({
                  type: 'success',
                  message: t(`appraisalMeeting.contract.changes.notifications.${operation}.success.title`),
                  description: t(`appraisalMeeting.contract.changes.notifications.${operation}.success.description`),
                })
                resetForm()
                reset()
              } else if (!res.data || !res.data.createOrUpdateChange.success) {
                setIsConfirmTargetBonusModalOpen(false)
                const errors = getValidationErrors(res.data!.createOrUpdateChange.error!, t)
                setErrors(errors)
              }
            }
          }}
          // Using validate rather then validationScheme so the whole context is accessible from yup.
          // If we would use validationScheme, validation for bonus wouldn't be able to access parent of parent
          // values (validFrom for example).
          validate={async (values) => {
            // this is a cleanup because antdesign sends an empty string
            // if you fill a field and then delete its contents
            // it says bonus & salary should be numbers but they can be strings in this case!
            if (typeof values.changes.bonus === 'string') {
              values.changes.bonus = undefined
            }
            if (typeof values.changes.salary === 'string') {
              values.changes.salary = undefined
            }

            let schema: ObjectSchema
            // This feature should be always enabled
            if (isFeatureEnabled) {
              if (countrySettings.hasGovernmentJobTitleReferenceLevel) {
                schema = getSimpleBrazilianContractChangeFormValidationSchema(t)
              } else {
                schema = getSimpleContractChangeFormValidationSchema(t)
              }
            } else {
              schema = hasNationalContractReference
                ? getItalianContractChangeFormValidationSchema(
                    currentContract.nationalContractReferenceLevel!.levels,
                    contractChangeValidationList,
                    employeeLevels,
                    t,
                  )
                : countrySettings.hasGovernmentJobTitleReferenceLevel
                ? getBrazilianContractChangeFormValidationSchema(
                    !currentContract.annualSalary ? 0 : currentContract.annualSalary.amount,
                    currentContract.annualSalary ? currentContract.annualSalary.currency : Currency.Eur,
                    t,
                    governmentJobTitlesByHiringOffice,
                  )
                : getContractChangeFormValidationSchema(
                    !currentContract.annualSalary ? 0 : currentContract.annualSalary.amount,
                    !currentContract.targetBonus ? 0 : currentContract.targetBonus.amount,
                    t,
                  )
            }

            try {
              await validateYupSchema(values, schema, false, values)
            } catch (err: any) {
              if (!!err && err.name === 'ValidationError') {
                return yupToFormErrors(err)
              } else {
                throw err
              }
            }
            return {} as FormikErrors<unknown>
          }}
        >
          {(props) => {
            const { isSubmitting, handleSubmit, values, resetForm, validateForm } = props
            const [selectedMonth, selectedYear] = values.validFrom ? values.validFrom.split(',') : [undefined, undefined]
            const selectedMonthNumber = selectedMonth ? Number(selectedMonth) : undefined

            // PER-3050: During an A/AU of an employee that joined Reply at least the year before the current one,
            // when inserting a contract change on his target bonus valid from any month that isn't January,
            // the confirmation pop-up should be visible
            const confirmTargetBonusModalNeeded =
              values.changes &&
              values.changes.bonus &&
              availableMonths.some((m) => {
                return m.month === 0
              }) &&
              selectedMonthNumber !== 0

            return (
              <>
                <ConfirmTargetBonusAmountModal
                  monthYear={values.validFrom ? `${t(`global.month.${selectedMonth}`)} ${selectedYear}` : ''}
                  isModalOpen={isConfirmTargetBonusModalOpen}
                  handleConfirm={handleSubmit}
                  closeModal={() => {
                    setIsConfirmTargetBonusModalOpen(false)
                  }}
                  isSubmitting={isSubmitting}
                />

                <PerModal
                  title={{
                    heading: isDeleting
                      ? t('appraisalMeeting.contract.changes.action.delete')
                      : isEdit
                      ? t('appraisalMeeting.contract.changes.action.edit')
                      : t('appraisalMeeting.contract.changes.action.createTooltip'),
                  }}
                  centered
                  visible={!isConfirmTargetBonusModalOpen}
                  onCancel={() => {
                    resetForm()
                    reset()
                  }}
                  footer={{
                    formikForm: confirmTargetBonusModalNeeded
                      ? undefined
                      : {
                          onSubmit: handleSubmit,
                        },
                    cancelBtn: {
                      onClick: () => {
                        resetForm()
                        reset()
                      },
                    },
                    confirmBtn: {
                      text: isDeleting ? t('global.button.delete') : t('global.button.save'),
                      eventHandler: {
                        onMouseDown:
                          confirmTargetBonusModalNeeded && !isDeleting
                            ? async () => {
                                const validationObject: FormikErrors<ContractChangeWithObjectPayload> = await validateForm()
                                if (!validationObject.changes && !validationObject.validFrom) {
                                  setIsConfirmTargetBonusModalOpen(true)
                                }
                              }
                            : handleSubmit,
                      },
                      disabled: isSubmitting || invalidGovernmentJobTitle || !!unknownGovernmentJobTitleCurrencyConversions.length,
                      isProcessing: { active: isSubmitting },
                    },
                  }}
                >
                  {!unknownGovernmentJobTitleCurrencyConversions.length && (
                    <>
                      {isDeleting && (
                        <PerSpacer mb="l">
                          <PerFlexRow>
                            <PerText fontWeight="bold">{t('appraisalMeeting.contract.changes.deleteDescription')}</PerText>
                          </PerFlexRow>
                        </PerSpacer>
                      )}
                      {!isDeleting && (
                        <PerText mt="none" mb="l" appearance="flowing-text" color="secondary" align="left">
                          <div>{t('appraisalMeeting.contract.changes.note1')}</div>
                          <div>{t('appraisalMeeting.contract.changes.note2')}</div>
                          <div>{t('appraisalMeeting.contract.changes.note3', { appraisalSeasonYear })}</div>
                        </PerText>
                      )}

                      <PerFlexRow direction="row" mb="s">
                        <Col span={12}>
                          <PerText appearance="tile-heading" mb="xxxs" mt="xxxs">
                            {t('appraisalMeeting.contract.changes.form.validFrom')}
                          </PerText>
                        </Col>
                        <Col span={12}>
                          <PerFlexRow mb="l">
                            <Col span={24}>
                              <FormItem name="validFrom">
                                <FormikSelect
                                  name="validFrom"
                                  placeholder={t('appraisalMeeting.contract.changes.form.validFromPlaceholder')}
                                  disabled={isSubmitting || isDeleting}
                                  timezoneAndLocale={user}
                                  dateFormat="MY"
                                  size="small"
                                  style={{ width: '100%' }}
                                  value={!!values.validFrom ? `${t(`global.month.${selectedMonth}`)} | ${selectedYear}` : undefined}
                                  optionFilterProp="children"
                                  data-e2e-month-dropdown
                                  data-testid="validFromSelect"
                                >
                                  {availableMonths.map((month) => (
                                    <Option
                                      value={`${month.month},${month.year}`}
                                      key={`${month.month}_${month.year}`}
                                      data-e2e-month-option={`${month.month}_${month.year}`}
                                    >
                                      {`${t(`global.month.${month.month}`)} | ${month.year}`}
                                    </Option>
                                  ))}
                                </FormikSelect>
                              </FormItem>
                            </Col>
                          </PerFlexRow>
                        </Col>
                      </PerFlexRow>

                      <PerFlexRow direction="row" mb="l">
                        <Col span={12}>
                          <PerText appearance="tile-heading">{t('appraisalMeeting.contract.changes.current.header')}</PerText>
                        </Col>
                        <Col span={12}>
                          <PerText appearance="tile-heading">{t('appraisalMeeting.contract.changes.change.header')}</PerText>
                        </Col>
                      </PerFlexRow>

                      <FormikContractChangePayload
                        timezoneAndLocale={user}
                        name="changes"
                        currentContract={currentContract}
                        validFrom={{
                          month: selectedMonthNumber,
                          year: selectedYear ? Number(selectedYear) : undefined,
                        }}
                        careerPathEvaluation={careerPathEvaluation}
                        formValues={values.changes}
                        scheduledChanges={scheduledChanges}
                        isEdit={isEdit}
                        isEmployeeLevelValidationEnabled={isEmployeeLevelValidationEnabled}
                        isPreparation={isPreparation}
                        contractChangeValidationList={contractChangeValidationList}
                        governmentJobTitles={
                          !!governmentJobTitlesByHiringOffice && !!governmentJobTitlesByHiringOffice.length
                            ? governmentJobTitlesByHiringOffice
                            : undefined
                        }
                        toggleInvalidGovernmentJobTitle={toggleInvalidGovernmentJobTitle}
                        countrySettings={countrySettings}
                        employeeLevels={employeeLevels}
                        meetingType={meeting.type}
                        appraisalSeasonYear={appraisalSeasonYear}
                        disabled={isDeleting}
                      />
                    </>
                  )}
                  {!!unknownGovernmentJobTitleCurrencyConversions.length && (
                    <PerText color="error">
                      {t('appraisalMeeting.contract.changes.unknownCurrencyConversion', {
                        governmentJobTitleCurrencies: unknownGovernmentJobTitleCurrencyConversions.toString().replace(',', ', '),
                        currentSalaryCurrency: currentContract.annualSalary!.currency,
                      })}
                    </PerText>
                  )}
                  {deleteError && (
                    <PerText color="error" fontSize="m" mt="m">
                      {deleteError}
                    </PerText>
                  )}
                </PerModal>
              </>
            )
          }}
        </Formik>
      )}
    </>
  )
}
